import { detect } from 'detect-browser';
import { JSDOM } from 'jsdom';
import mean from 'lodash/mean';
import max from 'lodash/max';
import min from 'lodash/min';
import sum from 'lodash/sum';
import first from 'lodash/first';
import last from 'lodash/last';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import uniq from 'lodash/uniq';
import range from 'lodash/range';
import get from 'lodash/get';
import difference from 'lodash/difference';
import 'lodash/pick';
import set from 'lodash/set';
import 'lodash/setWith';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';
import find from 'lodash/find';
import { group, groups, rollup, rollups } from 'd3-array';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isDate from 'lodash/isDate';
import isPlainObject from 'lodash/isPlainObject';
import maxBy from 'lodash/maxBy';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

var isBrowser = function isBrowser() {
  var browser = detect();
  return browser && browser.name !== "node";
};
var getDocument = function getDocument() {
  if (!isBrowser()) {
    var dom = new JSDOM("<!DOCTYPE html><head></head><body></body>");
    return dom.window.document;
  } else {
    return window.document;
  }
};
var RAWError =
/*#__PURE__*/
function (_Error) {
  _inheritsLoose(RAWError, _Error);

  function RAWError(message) {
    var _this;

    _this = _Error.call(this, message) || this;
    _this.name = 'RAWError';
    _this.message = message;
    return _this;
  }

  return RAWError;
}(_wrapNativeSuper(Error));

var aggregationsRegister = {};
function registerAggregation(name, fun) {
  aggregationsRegister[name] = fun;
}
function getAggregator(aggregatorExpression) {
  if (isFunction(aggregatorExpression)) {
    return aggregatorExpression;
  }

  if (isString(aggregatorExpression)) {
    if (aggregationsRegister[aggregatorExpression]) {
      return aggregationsRegister[aggregatorExpression];
    } else {
      throw new RAWError("Aggregator \"" + aggregatorExpression + "\" is is not registered in RAW.");
    }
  }
}
function getAggregatorArray(aggregator, length) {
  return function (items) {
    return range(length).map(function (idx) {
      var aggregatorExpression = Array.isArray(aggregator) ? get(aggregator, idx, aggregator[0]) : aggregator;
      var aggr = getAggregator(aggregatorExpression);
      return aggr(items.map(function (i) {
        return i[idx];
      }));
    });
  };
} // Aggregators available in RAW
// general purpose

registerAggregation("last", last);
registerAggregation("first", first);
registerAggregation("count", function (items) {
  return items.length;
});
registerAggregation("countDistinct", function (items) {
  return uniq(items).length;
}); // numbers

registerAggregation("mean", mean);
registerAggregation("max", max);
registerAggregation("min", min);
registerAggregation("sum", sum); //string

var commaSeparated = function commaSeparated(items) {
  return items.join(",");
};

var tabSeparated = function tabSeparated(items) {
  return items.join("\t");
};

var newLineSeparated = function newLineSeparated(items) {
  return items.join("\n");
};

var itemsList = function itemsList(items) {
  return items;
};

var itemsUniq = function itemsUniq(items) {
  return uniq(items);
};

registerAggregation("csv", commaSeparated);
registerAggregation("csvDistinct", function (items) {
  return commaSeparated(uniq(items));
});
registerAggregation("commaSeparated", commaSeparated);
registerAggregation("tsv", tabSeparated);
registerAggregation("tsvDistinct", function (items) {
  return tabSeparated(uniq(items));
});
registerAggregation("tabSeparated", tabSeparated);
registerAggregation("newLineSeparated", newLineSeparated);
registerAggregation("list", itemsList);
registerAggregation("distinct", itemsUniq);

function groupByAsMap(arr, getter) {
  return arr.reduce(function (obj, item) {
    var groupKey = get(item, getter);

    if (!obj.has(groupKey)) {
      obj.set(groupKey, []);
    }

    obj.set(groupKey, obj.get(groupKey).concat([item]));
    return obj;
  }, new Map());
}

/**
 * dimensions validator
 *
 * @param {array} dimensions
 */

function validateMapperDefinition(dimensions) {
  if (!Array.isArray(dimensions)) {
    throw new RAWError("dimesions must be an array");
  }

  if (dimensions.length === 0) {
    throw new RAWError("empty dimensions");
  }

  var getters = dimensions.filter(function (d) {
    return d.operation === "get";
  });
  var grouperTypes = ["rollup", "rollups"];
  var grouperDimension = dimensions.filter(function (d) {
    return grouperTypes.indexOf(d.operation) !== -1;
  });

  if (grouperDimension.length > 1) {
    throw new RAWError("only one operation among " + grouperTypes.join(",") + " is allowed");
  }

  if (getters.length === 0 && !grouperDimension.length) {
    throw new RAWError("at least one get operation must be present in a dimension set, or an operation among " + grouperTypes.join(",") + " must be specified");
  }

  if (getters.length > 0 && grouperDimension.length) {
    throw new RAWError("'" + grouperDimension[0].operation + "' operation was specified, you cannot define other get operations");
  }
}
/**
 * mapping validator
 *
 * @param {array} dimensions
 * @param {object} mapping
 *
 */

function validateMapping(dimensions, _mapping) {
  // validating that all required dimensions are provided to mapping
  var mapping = hydrateProxies(dimensions, _mapping);
  var requiredDimensions = dimensions.filter(function (d) {
    return d.required;
  }).map(function (d) {
    return d.id;
  }).sort();
  var providedDimensions = Object.keys(mapping).filter(function (k) {
    return get(mapping[k], "value");
  }).sort();
  var missing = difference(requiredDimensions, providedDimensions);

  if (missing.length > 0) {
    throw new RAWError("Some required dimensions were not mapped. Missing ids are: " + missing.join(", "));
  } // #TODO: [future] if using registered functions check for existence
  // #TODO: [future] if using expressions check for existence


  return mapping;
}

function hydrateProxies(dimensions, mapping) {
  var m = mapValues(mapping, function (v) {
    return _extends({}, v, {
      value: Array.isArray(v.value) ? v.value : [v.value]
    });
  });
  var proxiesDimensions = dimensions.filter(function (dim) {
    return dim.operation === "proxy";
  });
  proxiesDimensions.forEach(function (dimension) {
    var targets = get(dimension, "targets");

    if (!targets) {
      return;
    }

    var targetDimensions = Object.keys(targets);
    targetDimensions.forEach(function (targetDimensionId) {
      var targetsMap = targets[targetDimensionId]; //should be an obj with keys as target expressions and values as source expressions

      Object.keys(targetsMap).forEach(function (targetExpression) {
        var sourceExpression = targetsMap[targetExpression];
        var value = get(mapping, "[" + dimension.id + "][" + sourceExpression + "]");

        if (!m[targetDimensionId]) {
          m[targetDimensionId] = {};
        }

        set(m[targetDimensionId], targetExpression, value);
      });
    });
  });
  return m;
}

function arrayGetter(names) {
  if (Array.isArray(names)) {
    return names.length === 1 ? function (item) {
      return get(item, names[0]);
    } : function (item) {
      return names.map(function (name) {
        return get(item, name);
      });
    };
  }

  return function (item) {
    return get(item, names);
  };
}
/**
 * mapper generator
 *
 * @param {array} dimensions
 * @param {object} mapping
 * @return {function} the mapper function
 */
// #TODO: REFACTOR


function mapper(dimensions, _mapping, types) {
  validateMapperDefinition(dimensions); // let mapping = hydrateProxies(dimensions, _mapping);
  // console.log("hydrated", mapping, _mapping)

  var mapping = validateMapping(dimensions, _mapping);

  var dimensionsById = keyBy(dimensions, "id");
  var mappingValues = mapValues(mapping, function (v) {
    return v.value;
  });
  var mappingConfigs = mapValues(mapping, function (v) {
    return get(v, "config");
  });
  var getDimensions = dimensions.filter(function (d) {
    return d.operation === "get" && mappingValues[d.id] !== undefined;
  }).map(function (g) {
    return g.id;
  });
  var groupAggregateDimension = get(find(dimensions, function (d) {
    return d.operation === "groupAggregate" && mappingValues[d.id] !== undefined;
  }), "id");
  var groupByDimension = get(find(dimensions, function (d) {
    return d.operation === "groupBy" && mappingValues[d.id] !== undefined;
  }), "id");
  var groupDimension = get(find(dimensions, function (d) {
    return d.operation === "group" && mappingValues[d.id] !== undefined;
  }), "id");
  var groupsDimension = get(find(dimensions, function (d) {
    return d.operation === "groups" && mappingValues[d.id] !== undefined;
  }), "id");
  var rollupDimension = get(find(dimensions, function (d) {
    return d.operation === "rollup" && mappingValues[d.id] !== undefined;
  }), "id");
  var rollupsDimension = get(find(dimensions, function (d) {
    return d.operation === "rollups" && mappingValues[d.id] !== undefined;
  }), "id"); //#TODO ... is this still needed?

  var hierarchyDimension = get(find(dimensions, function (d) {
    return d.operation === "hierarchy" && mappingValues[d.id] !== undefined;
  }), "id"); //#TODO: TAKE IN ACCOUNT GROUP AGGREGATE DUE TO FORMATS

  var formatAggregateDimensions = getDimensions.filter(function (id) {
    return get(mappingConfigs[id], "format");
  });
  var candidateGroupers = [groupByDimension, groupDimension, groupsDimension, rollupDimension, rollupsDimension].filter(function (x) {
    return !!x;
  });

  if (candidateGroupers.length > 1) {
    throw new RAWError("only one of these operations is allowed in a mapper definition: 'group', 'groups', 'groupBy', 'rollup', 'rollups'");
  }

  var grouperDimension;

  if (candidateGroupers.length) {
    grouperDimension = candidateGroupers[0];
  }

  var rollupGrouperDimension = rollupDimension || rollupsDimension;
  return function (data) {
    var tabularData; //apply grouping operations if any

    if (groupAggregateDimension) {
      // #todo: this is complex. allow only strings in this case
      var identifiers = flatten([mappingValues[groupAggregateDimension]]);
      var dataGroups = groupBy(data, function (row) {
        var labelPieces = identifiers.map(function (x) {
          return get(row, x);
        });
        return JSON.stringify(labelPieces);
      });
      tabularData = Object.keys(dataGroups).map(function (label) {
        var item = {};
        var group = dataGroups[label];
        item[groupAggregateDimension] = JSON.parse(label);

        if (item[groupAggregateDimension].length === 1) {
          item[groupAggregateDimension] = item[groupAggregateDimension][0];
        }

        getDimensions.forEach(function (getter) {
          var getterColumn = mappingValues[getter]; //#GET HERE

          var getterFunction = arrayGetter(getterColumn);
          var allData = group.map(function (d) {
            return getterFunction(d);
          });
          var getterInAggregator = identifiers.indexOf(getterColumn) !== -1;
          var aggregator = get(mappingConfigs[getter], "aggregation", getterInAggregator ? function (data) {
            return data[0];
          } : function (data) {
            return data.length;
          });
          var aggregatorFunction = Array.isArray(getterColumn) && getterColumn.length > 1 ? getAggregatorArray(aggregator, getterColumn.length) : getAggregator(aggregator);
          item[getter] = aggregatorFunction(allData);
        });

        if (groupDimension || groupsDimension) {
          if (Array.isArray(mappingValues[groupDimension])) {
            item[groupDimension] = mappingValues[groupDimension].map(function (v) {
              return get(group[0], v);
            });
          } else {
            item[groupDimension] = get(group[0], mappingValues[groupDimension]);
          }
        }

        return item;
      });
    } else {
      tabularData = data.map(function (row) {
        var item = {};
        getDimensions.forEach(function (id) {
          //#GET HERE
          var getterFunction = arrayGetter(mappingValues[id]);
          item[id] = getterFunction(row);
        });

        if (grouperDimension && mappingValues[grouperDimension]) {
          if (Array.isArray(mappingValues[grouperDimension])) {
            item[grouperDimension] = mappingValues[grouperDimension].map(function (v) {
              return get(row, v);
            });
          } else {
            item[grouperDimension] = get(row, mappingValues[grouperDimension]);
          }
        } // getter for rollup aggregation
        // notice that the name __leaf is used only internally.


        if (rollupGrouperDimension && mappingConfigs[rollupGrouperDimension]) {
          var rollupConfigAggregationTarget = get(mappingConfigs, "[" + rollupGrouperDimension + "].leafAggregation[1]");
          var getterFunction = arrayGetter(rollupConfigAggregationTarget);
          item["__leaf"] = getterFunction(row);
        }

        return item;
      });
    } //#TODO
    //apply hierarchy operation if any
    // if (hierarchyDimension) {
    //   // ...
    // }


    if (grouperDimension) {
      if (groupByDimension) {
        return groupByAsMap(tabularData, groupByDimension);
      }

      var grouperDims = Array.isArray(mappingValues[grouperDimension]) ? mappingValues[grouperDimension] : [mappingValues[grouperDimension]];
      var grouperGetters = range(grouperDims.length).map(function (idx) {
        return function (item) {
          return item[grouperDimension][idx];
        };
      });

      if (groupDimension) {
        return group.apply(void 0, [tabularData].concat(grouperGetters));
      }

      if (groupsDimension) {
        return groups.apply(void 0, [tabularData].concat(grouperGetters));
      }

      if (rollupGrouperDimension) {
        var rollupConfigAggregation = get(mappingConfigs, "[" + rollupGrouperDimension + "].leafAggregation");

        var rollupAggregation = function rollupAggregation(v) {
          return v.length;
        };

        if (rollupConfigAggregation) {
          if (!Array.isArray(rollupConfigAggregation) || rollupConfigAggregation.length !== 2) {
            throw new RAWError("Rollup aggregation should be an array with aggregation function and target column");
          }

          var aggName = rollupConfigAggregation[0],
              targetColumn = rollupConfigAggregation[1];
          var aggregatorFunction = Array.isArray(targetColumn) && targetColumn.length > 1 ? getAggregatorArray(aggName, targetColumn.length) : getAggregator(aggName);
          var leafGetter = arrayGetter("__leaf");

          var wrappedAggregatorFunction = function wrappedAggregatorFunction(items) {
            return aggregatorFunction(items.map(leafGetter));
          };

          rollupAggregation = wrappedAggregatorFunction;
        }

        var finalRollupFunction = rollupDimension ? rollup : rollups;
        return finalRollupFunction.apply(void 0, [tabularData, rollupAggregation].concat(grouperGetters));
      }
    }

    return tabularData;
  };
}

var defaultVisualOptions = {
  width: 500,
  height: 500,
  background: '#FFFFFF'
};

var RAWBase =
/*#__PURE__*/
function () {
  function RAWBase(visualModel, data, mapping, visualOptions) {
    this._visualModel = visualModel;
    this._data = data;
    this._mapping = mapping;
    this._visualOptions = visualOptions;
  }

  var _proto = RAWBase.prototype;

  _proto.data = function data(_data) {
    if (!arguments.length) {
      return this._data;
    } // # TODO VALIDATE data


    return new RAWBase(this._visualModel, _data, this._mapping, this._visualOptions);
  };

  _proto.getContainer = function getContainer() {
    var document = getDocument();
    var container = document.createElement('svg');

    var options = _extends({}, defaultVisualOptions, {}, this._visualOptions);

    container.setAttribute('width', options.width);
    container.setAttribute('height', options.height);
    container.style['background-color'] = options.background;
    return container;
  };

  _proto.mapData = function mapData() {
    var mapRow = function mapRow(r) {
      var o = {};
      Object.keys(this._mapping).forEach(function (key) {
        o[key] = row[mapping[key]];
      });
    };

    var data = this._data || [];
    return data.map(mapRow);
  };

  _proto.renderToDOM = function renderToDOM(node) {
    var container = this.getContainer();
    var vizData = this.mapData();

    this._visualModel.render(container, vizData, this._visualOptions);

    node.innerHTML = "";
    node.appendChild(container);
    return new RAWDOM(node, this._visualModel, this._data, this._mapping, this._visualOptions);
  } //mapping
  ;

  return RAWBase;
}();

var RAWDOM =
/*#__PURE__*/
function (_RAWBase) {
  _inheritsLoose(RAWDOM, _RAWBase);

  function RAWDOM(node) {
    var _this;

    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    _this = _RAWBase.call.apply(_RAWBase, [this].concat(args)) || this;
    _this._node = node;
    return _this;
  }

  return RAWDOM;
}(RAWBase);

function raw(visualModel, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      data = _options.data,
      mapping = _options.mapping,
      visualOptions = _options.visualOptions;
  return new RAWBase(visualModel, data, mapping, visualOptions);
}

dayjs.extend(customParseFormat);
dayjs.extend(utc);

function getType(dataType) {
  if (isPlainObject(dataType)) {
    return getType(dataType.type);
  }

  if (isString(dataType)) {
    switch (dataType.toLowerCase()) {
      case 'string':
        return String;

      case 'number':
        return Number;

      case 'boolean':
        return Boolean;

      case 'date':
        return Date;

      default:
        return String;
    }
  }

  return dataType;
}

function getFormatter(dataType) {
  if (!isPlainObject(dataType)) {
    return undefined;
  }

  if (isFunction(dataType.decode)) {
    return dataType.decode;
  }

  if (getType(dataType) === Date) {
    if (isString(dataType.dateFormat)) {
      return function (value) {
        return dayjs(value, dataType.dateFormat).utc().toDate();
      };
    }
  }

  if (dataType.type === Boolean) ;

  return undefined;
}

function getValueType(value, strict) {
  var jsonValue = value;

  if (!strict) {
    try {
      jsonValue = JSON.parse(value);
    } catch (err) {}
  }

  if (isNumber(jsonValue)) {
    return Number;
  }

  if (isBoolean(jsonValue)) {
    return Boolean;
  }

  if (isDate(value)) {
    return Date;
  }

  return String;
}

function castTypeToString(type) {
  return type.name ? type.name.toLowerCase() : type;
}

function inferTypes(data, strict) {
  var candidateTypes = {};

  if (!Array.isArray(data)) {
    return candidateTypes;
  }

  data.forEach(function (datum) {
    Object.keys(datum).forEach(function (key) {
      if (candidateTypes[key] === undefined) {
        candidateTypes[key] = [];
      }

      var inferredType = getValueType(datum[key], strict);
      candidateTypes[key].push(castTypeToString(inferredType));
    });
  });
  var inferredTypes = {};
  Object.keys(candidateTypes).map(function (k) {
    var counts = {};
    candidateTypes[k].forEach(function (type) {
      if (!counts[type]) {
        counts[type] = {
          count: 0,
          value: type
        };
      }

      counts[type].count += 1;
    });
    var mostFrequentTypeKey = maxBy(Object.keys(counts), function (t) {
      return counts[t].count;
    });
    inferredTypes[k] = counts[mostFrequentTypeKey].value;
  });
  return inferredTypes;
}

function basicGetter(rowValue, dataType) {
  if (rowValue === null || rowValue === undefined) {
    return null;
  }

  return dataType(rowValue);
} // builds a parser function


function rowParser(types, onError) {
  var propGetters = {};
  Object.keys(types).forEach(function (k) {
    var dataType = types[k];
    var type = getType(dataType);
    var formatter = getFormatter(dataType);

    propGetters[k] = function (row) {
      var rowValue = get(row, k);
      var formattedValue = formatter ? formatter(rowValue) : rowValue;
      return basicGetter(formattedValue, formatter ? function (x) {
        return x;
      } : type);
    };
  });
  return function (row) {
    var error = {};
    var out = {};
    Object.keys(propGetters).forEach(function (k) {
      var getter = propGetters[k];

      try {
        out[k] = getter(row);
      } catch (err) {
        out[k] = null;
        error[k] = err;
      }
    });

    if (Object.keys(error).length) {
      onError && onError(error);
    }

    return out;
  };
}

function parseRows(data, dataTypes) {
  var errors = [];
  var parser = rowParser(dataTypes, function (error) {
    return errors.push(error);
  });
  var dataset = data.map(parser);
  return [dataset, errors];
}

function parseDataset(data, types) {
  var dataTypes = types || inferTypes(data);

  var _parseRows = parseRows(data, dataTypes),
      dataset = _parseRows[0],
      errors = _parseRows[1];

  return [dataset, dataTypes, errors];
}

export { inferTypes, mapper as makeMapper, parseDataset, raw, validateMapperDefinition, validateMapping };
